import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Clock, Package, FileCheck, FileText, CheckCircle2, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_BASE = `${BACKEND_URL}/api`;

const statusMeta = {
  draft: { label: "Draft", tone: "bg-slate-200 text-slate-800" },
  in_progress: { label: "In Progress", tone: "bg-yellow-100 text-yellow-900 border border-yellow-300" },
  completed: { label: "Completed", tone: "bg-emerald-100 text-emerald-900 border border-emerald-300" },
  closed: { label: "Closed", tone: "bg-blue-100 text-blue-900 border border-blue-300" },
};

export default function PublicTrack() {
  const { id } = useParams();
  const [jc, setJc] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    axios.get(`${API_BASE}/public/job-cards/${id}`)
      .then(({ data }) => setJc(data))
      .catch((e) => setErr(e.response?.data?.detail || "Job card not found"));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-4">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "var(--font-heading)" }}>Not found</h1>
          <p className="text-slate-600 text-sm">{err}</p>
        </div>
      </div>
    );
  }

  if (!jc) {
    return <div className="min-h-screen grid place-items-center text-slate-500 text-sm uppercase tracking-widest">Loading…</div>;
  }

  const meta = statusMeta[jc.status] || statusMeta.draft;
  const approvalTone = jc.approval_status === "approved"
    ? "bg-emerald-100 text-emerald-900 border-emerald-300"
    : jc.approval_status === "rejected"
    ? "bg-red-100 text-red-900 border-red-300"
    : "bg-slate-100 text-slate-700 border-slate-300";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-track-page">
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-2xl mx-auto px-6 py-6 flex items-center gap-3">
          <img src="/brand/logo.png" alt="CTI" className="w-11 h-11 rounded bg-white p-0.5 object-contain" />
          <div>
            <div className="text-base font-semibold tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>CTI Printing Line</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Job Status</div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Job header */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Job Card No.</div>
          <div className="text-3xl font-mono text-slate-900 mb-4" data-testid="track-jc-no">{jc.job_card_no}</div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Customer</div>
              <div className="font-medium text-slate-900">{jc.customer_name}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">PO No.</div>
              <div className="font-mono text-slate-900">{jc.customer_po}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Item</div>
              <div className="text-slate-900">{jc.item_name || "—"} {jc.item_type && <span className="text-slate-500 text-xs">· {jc.item_type}</span>}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Due</div>
              <div className="text-slate-900">{jc.due_date || "—"}</div>
            </div>
          </div>
        </div>

        {/* Product description / notes */}
        {(jc.product_description || jc.notes) && (
          <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 space-y-3">
            {jc.product_description && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Product Description</div>
                <div className="text-sm text-slate-900 mt-0.5 whitespace-pre-wrap">{jc.product_description}</div>
              </div>
            )}
            {jc.notes && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Notes</div>
                <div className="text-sm text-slate-900 mt-0.5 whitespace-pre-wrap">{jc.notes}</div>
              </div>
            )}
          </div>
        )}

        {/* Status */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Current Status</div>
          <div className={`inline-block px-3 py-1.5 rounded text-xs uppercase tracking-widest font-medium ${meta.tone}`} data-testid="track-status">{meta.label}</div>
          <div className="mt-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <div className="text-slate-600">Production progress</div>
              <div className="font-mono text-slate-900" data-testid="track-progress">{jc.progress_pct}%</div>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-yellow-400 transition-all" style={{ width: `${jc.progress_pct}%` }} />
            </div>
            <div className="mt-2 text-xs text-slate-500 flex justify-between">
              <span>Good qty: <span className="font-mono">{jc.good_qty}</span></span>
              <span>Ordered: <span className="font-mono">{jc.ordered_qty}</span></span>
            </div>
          </div>
        </div>

        {/* Attachments (per-item + legacy) */}
        {(() => {
          const perItem = (jc.items || [])
            .map((it, idx) => ({ idx, name: it.item_name || `Item ${idx + 1}`, files: it.artwork_files || [] }))
            .filter((g) => g.files.length > 0);
          const legacy = jc.artwork_files || [];
          const hasAny = perItem.length > 0 || legacy.length > 0;
          if (!hasAny) return null;
          const renderFile = (f) => {
            const url = `${API_BASE}/public/files/${f.id}`;
            const isImage = (f.content_type || "").startsWith("image/");
            const isPdf = f.content_type === "application/pdf";
            return (
              <div key={f.id} className="border border-slate-200 rounded overflow-hidden bg-slate-50" data-testid={`track-file-${f.id}`}>
                <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-white text-sm">
                  <FileText className="w-4 h-4 text-slate-500" />
                  <span className="truncate flex-1 font-medium">{f.original_filename}</span>
                  <span className="text-xs text-slate-500 font-mono">{(f.size / 1024).toFixed(1)} KB</span>
                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-700 hover:text-slate-900 font-medium">Open</a>
                </div>
                {isImage ? (
                  <a href={url} target="_blank" rel="noopener noreferrer" className="block">
                    <img src={url} alt={f.original_filename} className="max-h-[520px] w-full object-contain bg-white" />
                  </a>
                ) : isPdf ? (
                  <iframe src={url} title={f.original_filename} className="w-full bg-white" style={{ height: "520px", border: "none" }} />
                ) : (
                  <div className="p-4 bg-white">
                    <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 font-medium">
                      <FileText className="w-4 h-4" /> Download {f.original_filename}
                    </a>
                  </div>
                )}
              </div>
            );
          };
          return (
            <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-4 h-4 text-slate-500" />
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Attached Artwork</div>
              </div>
              <div className="space-y-6">
                {perItem.map((g) => (
                  <div key={g.idx}>
                    <div className="text-[11px] uppercase tracking-widest text-slate-600 font-semibold mb-2">
                      #{g.idx + 1} · {g.name}
                    </div>
                    <div className="space-y-3">{g.files.map(renderFile)}</div>
                  </div>
                ))}
                {legacy.length > 0 && (
                  <div>
                    {perItem.length > 0 && <div className="text-[11px] uppercase tracking-widest text-slate-600 font-semibold mb-2">Other</div>}
                    <div className="space-y-3">{legacy.map(renderFile)}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Approval + Checklist */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <FileCheck className="w-4 h-4 text-slate-500" />
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Artwork Sign-off</div>
            </div>
            <div className={`inline-block px-2.5 py-1 rounded text-[11px] uppercase tracking-wider font-medium border ${approvalTone}`} data-testid="track-approval">
              {jc.approval_status || "Pending"}
            </div>
            {jc.approver_name && (
              <div className="text-xs text-slate-500 mt-2">by {jc.approver_name}</div>
            )}
            {jc.approval_signed_at && (
              <div className="text-xs text-slate-500 mt-1">{new Date(jc.approval_signed_at).toLocaleString()}</div>
            )}
            {jc.artwork_version && (
              <div className="text-xs text-slate-500 mt-1">Order Quality: <span className="font-mono">{jc.artwork_version}</span></div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-4 h-4 text-slate-500" />
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Checklist</div>
            </div>
            <div className="text-2xl font-mono text-slate-900" data-testid="track-checklist">
              {jc.checklist_progress.done}<span className="text-slate-400 text-lg">/{jc.checklist_progress.total}</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">items complete</div>
          </div>
        </div>

        {/* Public Approval Form */}
        {!jc.approval_status && (
          <PublicApprovalForm jobId={jc.id} onDone={load} />
        )}

        <div className="text-center text-xs text-slate-500 pt-4 flex items-center justify-center gap-2">
          <Clock className="w-3 h-3" /> Last updated {new Date(jc.updated_at || jc.created_at).toLocaleString()}
        </div>
        <div className="text-center text-[10px] text-slate-400 uppercase tracking-widest pb-6">© CTI Printing Line</div>
      </div>
    </div>
  );
}

function PublicApprovalForm({ jobId, onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const submit = async (decision) => {
    setErr("");
    setMsg("");
    if (!name.trim()) { setErr("Please enter your name"); return; }
    if (decision === "rejected" && !comments.trim()) { setErr("Please add a comment when rejecting"); return; }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/public/job-cards/${jobId}/approval`, {
        approver_name: name, approver_email: email, decision, comments,
      });
      setMsg(`Thank you — your ${decision === "approved" ? "approval" : "rejection"} has been recorded.`);
      setTimeout(() => onDone && onDone(), 900);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border-2 border-yellow-400 rounded-lg p-5 sm:p-6" data-testid="public-approval-form">
      <div className="flex items-center gap-2 mb-1">
        <FileCheck className="w-4 h-4 text-yellow-600" />
        <div className="text-[10px] uppercase tracking-widest text-yellow-700 font-semibold">Customer Sign-off Required</div>
      </div>
      <h3 className="text-xl font-semibold text-slate-900 mb-1" style={{ fontFamily: "var(--font-heading)" }}>
        Approve artwork from your phone
      </h3>
      <p className="text-sm text-slate-600 mb-4">
        Please review the attached artwork above and confirm whether it can proceed to production.
      </p>

      {msg && (
        <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-800" data-testid="approval-success">
          <CheckCircle2 className="w-4 h-4 inline mr-1" /> {msg}
        </div>
      )}
      {err && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800" data-testid="approval-error">
          <XCircle className="w-4 h-4 inline mr-1" /> {err}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-slate-600 font-medium block mb-1">Your name *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. John Doe"
            className="w-full border border-slate-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            data-testid="public-approval-name"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-slate-600 font-medium block mb-1">Email (optional)</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="john@company.com"
            className="w-full border border-slate-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            data-testid="public-approval-email"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-slate-600 font-medium block mb-1">Comments (required for reject)</label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={2}
            placeholder="Any notes or changes required"
            className="w-full border border-slate-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            data-testid="public-approval-comments"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-5">
        <button
          type="button"
          onClick={() => submit("rejected")}
          disabled={busy}
          className="w-full px-4 py-3 rounded border-2 border-red-300 bg-white text-red-700 font-medium text-sm hover:bg-red-50 disabled:opacity-50 transition-colors"
          data-testid="public-reject-btn"
        >
          <XCircle className="w-4 h-4 inline mr-1" /> Reject
        </button>
        <button
          type="button"
          onClick={() => submit("approved")}
          disabled={busy}
          className="w-full px-4 py-3 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm disabled:opacity-50 transition-colors"
          data-testid="public-approve-btn"
        >
          <CheckCircle2 className="w-4 h-4 inline mr-1" /> {busy ? "Submitting…" : "Approve"}
        </button>
      </div>
      <p className="text-[10px] text-slate-500 mt-3 text-center">
        Your decision is recorded with timestamp and cannot be changed once submitted.
      </p>
    </div>
  );
}
